// Central achievements catalog with emoji and playful descriptions
export type AchievementRarity = 'common' | 'rare' | 'epic' | 'legendary';

export interface AchievementDef {
  id: string; // canonical key (snake_case)
  title: string; // display title (FR)
  emoji: string; // leading emoji
  description: string; // playful description (FR)
  colorScheme?: 'gray' | 'green' | 'yellow' | 'orange' | 'red' | 'purple' | 'pink' | 'blue' | 'teal' | 'cyan';
  rarity?: AchievementRarity;
}

// Utility to normalize ids/titles for fuzzy matching with backend-provided names
export const normalizeKey = (s: string) =>
  (s || '')
    .toLowerCase()
    .normalize('NFD')
    // Remove diacritics using explicit combining marks range for wider runtime support
    .replace(/[\u0300-\u036f]+/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

export const ALL_ACHIEVEMENTS: AchievementDef[] = [
  {
    id: 'first_win',
    title: 'Première victoire',
    emoji: '🥇',
    description: 'Gagnez votre toute première partie.',
    colorScheme: 'yellow',
    rarity: 'common',
  },
  {
    id: 'speed_demon',
    title: 'Speedy Gonzales',
    emoji: '⚡',
    description: 'Répondez correctement en moins de 1,5 seconde.',
    colorScheme: 'orange',
    rarity: 'rare',
  },
  {
    id: 'perfect_game',
    title: 'Sans faute',
    emoji: '🎯',
    description: 'Terminez une partie avec 100% de bonnes réponses.',
    colorScheme: 'green',
    rarity: 'epic',
  },
  {
    id: 'streak_5',
    title: 'Chaud bouillant',
    emoji: '🔥',
    description: 'Atteignez une série de 5 réponses correctes.',
    colorScheme: 'red',
    rarity: 'common',
  },
  {
    id: 'streak_10',
    title: 'En feu',
    emoji: '🔥🔥',
    description: 'Atteignez une série de 10 réponses correctes.',
    colorScheme: 'red',
    rarity: 'rare',
  },
  {
    id: 'multiplayer_first',
    title: 'Sociable',
    emoji: '👥',
    description: 'Jouez votre première partie multijoueur.',
    colorScheme: 'blue',
    rarity: 'common',
  },
  {
    id: 'comeback',
    title: 'Remontada',
    emoji: '🚀',
    description: 'Gagnez une partie après avoir été mené.',
    colorScheme: 'purple',
    rarity: 'epic',
  },
  {
    id: 'fastest_answer',
    title: 'Éclair',
    emoji: '💥',
    description: 'Réalisez la réponse correcte la plus rapide de la partie.',
    colorScheme: 'orange',
    rarity: 'rare',
  },
  {
    id: 'night_owl',
    title: 'Oiseau de nuit',
    emoji: '🌙',
    description: 'Jouez une partie après 23h.',
    colorScheme: 'cyan',
    rarity: 'rare',
  },
  {
    id: 'category_master',
    title: 'Maître de Catégorie',
    emoji: '📚',
    description: 'Gagnez une partie sur votre catégorie favorite.',
    colorScheme: 'teal',
    rarity: 'rare',
  },
  {
    id: 'lucky_spin',
    title: 'Main chanceuse',
    emoji: '🍀',
    description: 'Bénéficiez d’un pouvoir Mystère positif et gagnez.',
    colorScheme: 'green',
    rarity: 'rare',
  },
  {
    id: 'nuker',
    title: 'Docteur Apocalypse',
    emoji: '☢️',
    description: 'Déclenchez une Nuke en multijoueur.',
    colorScheme: 'pink',
    rarity: 'legendary',
  },
  // Server-side names coverage
  {
    id: 'premier_pas',
    title: 'Premier pas',
    emoji: '👣',
    description: 'Jouez votre toute première partie.',
    colorScheme: 'blue',
    rarity: 'common',
  },
  {
    id: 'habitude_10_parties',
    title: 'Habitué',
    emoji: '🔁',
    description: 'Jouez 10 parties au total.',
    colorScheme: 'cyan',
    rarity: 'common',
  },
  {
    id: 'veteran_50_parties',
    title: 'Vétéran',
    emoji: '🛡️',
    description: 'Jouez 50 parties au total.',
    colorScheme: 'yellow',
    rarity: 'rare',
  },
  {
    id: 'maitre_100_parties',
    title: 'Maître',
    emoji: '🏯',
    description: 'Jouez 100 parties au total.',
    colorScheme: 'purple',
    rarity: 'epic',
  },
  {
    id: 'tireur_d_elite',
    title: "Tireur d'élite",
    emoji: '🎯',
    description: 'Atteignez 80% de précision (min. 50 questions).',
    colorScheme: 'green',
    rarity: 'rare',
  },
  {
    id: 'perfectionniste',
    title: 'Perfectionniste',
    emoji: '✨',
    description: 'Atteignez 95% de précision (min. 100 questions).',
    colorScheme: 'green',
    rarity: 'epic',
  },
  {
    id: 'en_feu',
    title: 'En feu !',
    emoji: '🔥',
    description: 'Atteignez une série de 10 bonnes réponses.',
    colorScheme: 'red',
    rarity: 'rare',
  },
  {
    id: 'inarretable',
    title: 'Inarrêtable',
    emoji: '🚀',
    description: 'Atteignez une série de 20 bonnes réponses.',
    colorScheme: 'red',
    rarity: 'epic',
  },
  {
    id: 'gagnant',
    title: 'Gagnant',
    emoji: '🏆',
    description: 'Remportez 5 victoires.',
    colorScheme: 'yellow',
    rarity: 'common',
  },
  {
    id: 'champion',
    title: 'Champion',
    emoji: '👑',
    description: 'Remportez 25 victoires.',
    colorScheme: 'yellow',
    rarity: 'rare',
  },
  {
    id: 'maitre_du_mystere',
    title: 'Maître du mystère',
    emoji: '🎩',
    description: 'Réalisez 10 attaques Mystère réussies.',
    colorScheme: 'pink',
    rarity: 'rare',
  },
  // SmartClash (server-awarded names)
  {
    id: 'smartclash_createur',
    title: 'SmartClash: Créateur',
    emoji: '🛠️',
    description: 'Créez votre premier défi SmartClash.',
    colorScheme: 'blue',
    rarity: 'common',
  },
  {
    id: 'smartclash_premiere_tentative',
    title: 'SmartClash: Première tentative',
    emoji: '🎯',
    description: 'Soumettez votre première tentative sur un défi.',
    colorScheme: 'green',
    rarity: 'common',
  },
  {
    id: 'smartclash_10_tentatives',
    title: 'SmartClash: 10 tentatives',
    emoji: '🔟',
    description: 'Atteignez 10 tentatives SmartClash.',
    colorScheme: 'teal',
    rarity: 'rare',
  },
  {
    id: 'smartclash_score_60_plus',
    title: 'SmartClash: Score 60+',
    emoji: '🚀',
    description: 'Obtenez 60 points ou plus sur votre meilleur score.',
    colorScheme: 'yellow',
    rarity: 'rare',
  },
  {
    id: 'smartclash_10_bonnes_reponses',
    title: 'SmartClash: 10 bonnes réponses',
    emoji: '✅',
    description: 'Atteignez 10 bonnes réponses sur votre meilleure tentative.',
    colorScheme: 'green',
    rarity: 'rare',
  },
  {
    id: 'smartclash_sprint_40s',
    title: 'SmartClash: Sprint 40s',
    emoji: '⏱️',
    description: 'Réalisez votre meilleure tentative en 40s ou moins.',
    colorScheme: 'orange',
    rarity: 'epic',
  },
  // Daily Streak (client-side pretty labels for server-awarded names)
  {
    id: 'daily_streak_first_day',
    title: 'Daily Streak: Premier jour',
    emoji: '📅',
    description: 'Votre première participation au Daily Streak.',
    colorScheme: 'blue',
    rarity: 'common',
  },
  {
    id: 'daily_streak_7_days',
    title: 'Daily Streak: 7 jours',
    emoji: '🗓️',
    description: '7 participations au Daily Streak.',
    colorScheme: 'teal',
    rarity: 'rare',
  },
  {
    id: 'daily_streak_score_10',
    title: 'Daily Streak: Score 10+',
    emoji: '🔟',
    description: 'Atteignez un score de 10 ou plus sur un Daily.',
    colorScheme: 'green',
    rarity: 'common',
  },
  {
    id: 'daily_streak_score_20',
    title: 'Daily Streak: Score 20+',
    emoji: '🚀',
    description: 'Atteignez un score de 20 ou plus sur un Daily.',
    colorScheme: 'yellow',
    rarity: 'epic',
  },
  {
    id: 'daily_streak_under_60s',
    title: 'Daily Streak: Moins de 60s',
    emoji: '⏱️',
    description: 'Réalisez votre meilleur score en moins de 60 secondes.',
    colorScheme: 'orange',
    rarity: 'rare',
  },
];

// Attempt to resolve a backend-provided achievement name to a catalog definition,
// including dynamic patterns like "Niveau X atteint !".
export function resolveAchievementDef(name: string): AchievementDef | null {
  const key = normalizeKey(name);

  // Direct match against catalog titles/ids
  const byId = ALL_ACHIEVEMENTS.find(a => normalizeKey(a.id) === key);
  if (byId) return byId;
  const byTitle = ALL_ACHIEVEMENTS.find(a => normalizeKey(a.title) === key);
  if (byTitle) return byTitle;

  // Dynamic: Niveau X atteint !
  const lvlMatch = key.match(/^niveau_(\d+)_atteint!?$/);
  if (lvlMatch) {
    const level = parseInt(lvlMatch[1], 10);
    return {
      id: `level_${level}`,
      title: `Niveau ${level} atteint !`,
      emoji: level >= 20 ? '👑' : level >= 10 ? '🥇' : '🎖️',
      description: `Bravo ! Vous avez atteint le niveau ${level}.`,
      colorScheme: level >= 20 ? 'purple' : level >= 10 ? 'yellow' : 'blue',
      rarity: level >= 20 ? 'epic' : 'rare',
    };
  }

  return null;
}
